import { filter, map } from "d3";
import { CAMERA_COLORS } from "../../../../config/index";

// Transform monthly data for multi-camera comparison mode
export const transformMonthlyDataForComparison = (monthlyData, processedCameraData, isTrafficView = false) => {
    if (!monthlyData || monthlyData.length === 0 || monthlyData.error) {
        return { cameraDatasets: [], allMonths: [] };
    }

    // Get unique months from all data
    const allMonths = [...new Set(monthlyData.map(d => d.month))].sort((a, b) => a - b);

    // Filter out "All" camera from comparison
    const cameras = processedCameraData.filter(cam => cam.camera_id !== 'All' && cam.camera_id !== null);

    // Create datasets for each camera
    const cameraDatasets = cameras.map((camera, index) => {
        // Filter data for this camera
        const cameraData = monthlyData.filter(d => d.camera_id === camera.camera_id);

        // Create aggregation map for this camera
        const monthAggregation = new Map();

        cameraData.forEach(d => {
            const month = d.month;
            const eventType = d.event_type;
            const totalCount = parseInt(d.total_count, 10);

            if (!monthAggregation.has(month)) {
                monthAggregation.set(month, {
                    'Line Crossing': 0,
                    'Reverse Line Crossing': 0
                });
            }

            const monthData = monthAggregation.get(month);
            monthData[eventType] += totalCount;
        });

        // Create data array with all months (fill missing months with 0)
        const dataArray = allMonths.map(month => {
            const monthData = monthAggregation.get(month) || {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0
            };

            if (isTrafficView) {
                // Return average for traffic view
                return (monthData['Line Crossing'] + monthData['Reverse Line Crossing']) / 2;
            } else {
                // Return "Line Crossing" (in count) for normal view
                return monthData['Line Crossing'];
            }
        });

        return {
            label: camera.camera_name || `Camera ${index + 1}`,
            data: dataArray,
            backgroundColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderWidth: 2,
            camera_id: camera.camera_id,
            hidden: false // Default to visible, can be toggled via legend
        };
    });

    return { cameraDatasets, allMonths };
};

// Transform weekly data for multi-camera comparison mode
export const transformWeeklyDataForComparison = (weeklyData, processedCameraData, isTrafficView = false) => {
    if (!weeklyData || weeklyData.length === 0 || weeklyData.error) {
        return { cameraDatasets: [], allWeeks: [] };
    }

    // Get unique weeks from all data
    const allWeeks = [...new Set(weeklyData.map(d => d.week))].sort((a, b) => a - b);

    // Filter out "All" camera from comparison
    const cameras = processedCameraData.filter(cam => cam.camera_id !== 'All' && cam.camera_id !== null);

    // Create datasets for each camera
    const cameraDatasets = cameras.map((camera, index) => {
        // Filter data for this camera
        const cameraData = weeklyData.filter(d => d.camera_id === camera.camera_id);

        // Create aggregation map for this camera
        const weekAggregation = new Map();

        cameraData.forEach(d => {
            const week = d.week;
            const eventType = d.event_type;
            const totalCount = parseInt(d.total_count, 10);

            if (!weekAggregation.has(week)) {
                weekAggregation.set(week, {
                    'Line Crossing': 0,
                    'Reverse Line Crossing': 0
                });
            }

            const weekData = weekAggregation.get(week);
            weekData[eventType] += totalCount;
        });

        // Create data array with all weeks (fill missing weeks with 0)
        const dataArray = allWeeks.map(week => {
            const weekData = weekAggregation.get(week) || {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0
            };

            if (isTrafficView) {
                // Return average for traffic view
                return (weekData['Line Crossing'] + weekData['Reverse Line Crossing']) / 2;
            } else {
                // Return "Line Crossing" (in count) for normal view
                return weekData['Line Crossing'];
            }
        });

        return {
            label: camera.camera_name || `Camera ${index + 1}`,
            data: dataArray,
            backgroundColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderWidth: 2,
            camera_id: camera.camera_id,
            hidden: false
        };
    });

    return { cameraDatasets, allWeeks };
};

// Transform daily data for multi-camera comparison mode
export const transformDailyDataForComparison = (dailyData, processedCameraData, isTrafficView = false) => {
    if (!dailyData || dailyData.length === 0 || dailyData.error) {
        return { cameraDatasets: [], allDays: [] };
    }

    // Get unique days from all data (sorted by day_number)
    const uniqueDays = [...new Set(dailyData.map(d => JSON.stringify({ day: d.day, day_number: d.day_number })))]
        .map(str => JSON.parse(str))
        .sort((a, b) => a.day_number - b.day_number);

    const allDays = uniqueDays.map(d => d.day);

    // Filter out "All" camera from comparison
    const cameras = processedCameraData.filter(cam => cam.camera_id !== 'All' && cam.camera_id !== null);

    // Create datasets for each camera
    const cameraDatasets = cameras.map((camera, index) => {
        // Filter data for this camera
        const cameraData = dailyData.filter(d => d.camera_id === camera.camera_id);

        // Create aggregation map for this camera
        const dayAggregation = new Map();

        cameraData.forEach(d => {
            const day = d.day;
            const eventType = d.event_type;
            const totalCount = parseInt(d.total_count, 10);

            if (!dayAggregation.has(day)) {
                dayAggregation.set(day, {
                    'Line Crossing': 0,
                    'Reverse Line Crossing': 0
                });
            }

            const dayData = dayAggregation.get(day);
            dayData[eventType] += totalCount;
        });

        // Create data array with all days (fill missing days with 0)
        const dataArray = allDays.map(day => {
            const dayData = dayAggregation.get(day) || {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0
            };

            if (isTrafficView) {
                // Return average for traffic view
                return (dayData['Line Crossing'] + dayData['Reverse Line Crossing']) / 2;
            } else {
                // Return "Line Crossing" (in count) for normal view
                return dayData['Line Crossing'];
            }
        });

        return {
            label: camera.camera_name || `Camera ${index + 1}`,
            data: dataArray,
            backgroundColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderWidth: 2,
            camera_id: camera.camera_id,
            hidden: false
        };
    });

    return { cameraDatasets, allDays };
};

// Transform hourly data for multi-camera comparison mode
export const transformHourlyDataForComparison = (hourlyData, processedCameraData, isTrafficView = false) => {
    if (!hourlyData || hourlyData.length === 0 || hourlyData.error) {
        return { cameraDatasets: [], allHours: [] };
    }

    // Get unique hours from all data (sorted)
    const uniqueHours = [...new Set(hourlyData.map(d => d.hour))].sort((a, b) => a - b);

    // Get hour displays (like "12 AM", "1 PM", etc.)
    const allHours = uniqueHours;
    const hourDisplays = uniqueHours.map(hour => {
        const sample = hourlyData.find(d => d.hour === hour);
        return sample?.hour_display || `${hour}:00`;
    });

    // Filter out "All" camera from comparison
    const cameras = processedCameraData.filter(cam => cam.camera_id !== 'All' && cam.camera_id !== null);

    // Create datasets for each camera
    const cameraDatasets = cameras.map((camera, index) => {
        // Filter data for this camera
        const cameraData = hourlyData.filter(d => d.camera_id === camera.camera_id);

        // Create aggregation map for this camera
        const hourAggregation = new Map();

        cameraData.forEach(d => {
            const hour = d.hour;
            const eventType = d.event_type;
            const totalCount = parseInt(d.total_count, 10);

            if (!hourAggregation.has(hour)) {
                hourAggregation.set(hour, {
                    'Line Crossing': 0,
                    'Reverse Line Crossing': 0
                });
            }

            const hourData = hourAggregation.get(hour);
            hourData[eventType] += totalCount;
        });

        // Create data array with all hours (fill missing hours with 0)
        const dataArray = allHours.map(hour => {
            const hourData = hourAggregation.get(hour) || {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0
            };

            if (isTrafficView) {
                // Return average for traffic view
                return (hourData['Line Crossing'] + hourData['Reverse Line Crossing']) / 2;
            } else {
                // Return "Line Crossing" (in count) for normal view
                return hourData['Line Crossing'];
            }
        });

        return {
            label: camera.camera_name || `Camera ${index + 1}`,
            data: dataArray,
            backgroundColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderColor: CAMERA_COLORS[index % CAMERA_COLORS.length],
            borderWidth: 2,
            camera_id: camera.camera_id,
            hidden: false
        };
    });

    return { cameraDatasets, allHours, hourDisplays };
};

// aggregate monthly data by event type and camera name
export const transformMonthlyData = (monthlyData, selectedCameraId) => {
    // Return empty array if no data is available
    if (monthlyData.length === 0 || monthlyData.error) {
        return [];
    }

    let filteredData = [];

    if (selectedCameraId === 'All') {
        filteredData = monthlyData;
    } else {
        for (let i = 0; i < monthlyData.length; i++) {
            const row = monthlyData[i];
            if (row.camera_id === selectedCameraId) {
                filteredData.push(row);
            } else if (row.camera_id === null) {
                filteredData.push(row);
            } else {
                let hasMonth = filteredData.some(monthData => monthData.month === row.month);

                if (!hasMonth) {
                    const emptyMonth = {
                        'year': row.year,
                        'month': row.month,
                        'event_type': 'none',
                        'total_count': 0
                    }
                    filteredData.push(emptyMonth);
                }
            }
        }

    }

    // Temporary storage for aggregation
    const tempAggregation = new Map();

    // Iterate over filtered data to aggregate by month and event_type
    filteredData?.forEach(d => {
        const month = d.month;
        const eventType = d.event_type;
        const totalCount = parseInt(d.total_count, 10);

        // Initialize the month if it doesn't exist in tempAggregation. 
        if (!tempAggregation.has(month)) {
            tempAggregation.set(month, {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0,
                'year': d.year
            });
        }

        // Aggregate total count by event type
        const monthData = tempAggregation.get(month);
        monthData[eventType] += totalCount;

    });

    // Convert temp aggregation into the original structure format
    const aggregatedData = [];
    for (const [monthNum, monthData] of tempAggregation) {
        aggregatedData.push({
            year: monthData.year,
            month: parseInt(monthNum),
            total_count: monthData['Line Crossing'].toString(), // Keep the count for the first event type as reference
            camera_id: selectedCameraId === 'All' ? 'All' : filteredData[0]?.camera_id, // Either 'All' or the selected camera name
            event_types: {
                'Line Crossing': monthData['Line Crossing'],
                'Reverse Line Crossing': monthData['Reverse Line Crossing']
            }
        });
    }
    // // console.log("aggregatedData monthly count data: ", aggregatedData);
    return aggregatedData;
};

// aggregate weekly data by event type and camera name
export const transformWeeklyData = (weeklyData, selectedCameraId) => {
    // return empty array if no data is available
    if (weeklyData.length === 0 || weeklyData.error) {
        return [];
    }


    let filteredData = [];

    if (selectedCameraId === 'All') {
        filteredData = weeklyData;
    } else {
        for (let i = 0; i < weeklyData.length; i++) {
            const row = weeklyData[i];
            if (row.camera_id === selectedCameraId) {
                filteredData.push(row);
            } else if (row.camera_id === null) {
                filteredData.push(row);
            } else {
                let hasWeek = filteredData.some(weekData => weekData.week === row.week);

                if (!hasWeek) {
                    const emptyMonth = {
                        'year': row.year,
                        'month': row.month,
                        'week': row.week,
                        'event_type': 'none',
                        'total_count': 0
                    }
                    filteredData.push(emptyMonth);
                }
            }
        }

    }

    // Temporary storage for aggregation
    const tempAggregation = {};

    // Iterate over filtered data to aggregate by month and event_type
    filteredData?.forEach(d => {
        const week = d.week;
        const eventType = d.event_type;
        const totalCount = parseInt(d.total_count, 10);

        // Initialize the month if it doesn't exist in tempAggregation
        if (!tempAggregation[week]) {
            tempAggregation[week] = {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0,
                'year': d.year,
                'month': d.month,
                'week_start_date': d.week_start_date
            };
        }

        // Aggregate total count by event type
        tempAggregation[week][eventType] += totalCount;
    });

    // Convert temp aggregation into the original structure format
    const aggregatedData = [];
    for (const week in tempAggregation) {
        aggregatedData.push({
            year: tempAggregation[week].year,
            month: tempAggregation[week].month,
            week: parseInt(week),
            week_start_date: tempAggregation[week].week_start_date,
            total_count: tempAggregation[week]['Line Crossing'].toString(), // Keep the count for the first event type as reference
            camera_id: selectedCameraId === 'All' ? 'All' : filteredData[0]?.camera_id, // Either 'All' or the selected camera name
            event_types: {
                'Line Crossing': tempAggregation[week]['Line Crossing'],
                'Reverse Line Crossing': tempAggregation[week]['Reverse Line Crossing']
            }
        });
    }

    // // console.log("Aggregated weekly count data: ", aggregatedData);

    return aggregatedData;
};

// aggregate daily data by event type and camera name
export const transformDailyData = (dailyData, selectedCameraId) => {
    // Return empty array if no data is available
    if (dailyData.length === 0 || dailyData.error) {
        return [];
    }

    let filteredData = [];

    if (selectedCameraId === 'All') {
        filteredData = dailyData;
    } else {
        for (let i = 0; i < dailyData.length; i++) {
            const row = dailyData[i];
            if (row.camera_id === selectedCameraId) {
                filteredData.push(row);
            } else if (row.camera_id === null) {
                filteredData.push(row);
            } else {
                let hasDay = filteredData.some(dayData => dayData.day_number === row.day_number);

                if (!hasDay) {
                    const emptyMonth = {
                        'year': row.year,
                        'day_number': row.day_number,
                        'day': row.day,
                        'total_count': 0
                    }
                    filteredData.push(emptyMonth);
                }
            }
        }

    }

    // Temporary storage for aggregation
    const tempAggregation = {};

    // Iterate over filtered data to aggregate by month and event_type
    filteredData?.forEach(d => {
        const dayNumber = d.day_number;
        const day = d.day;
        const eventType = d.event_type;
        const totalCount = parseInt(d.total_count, 10);

        // Initialize the month if it doesn't exist in tempAggregation
        if (!tempAggregation[day]) {
            tempAggregation[day] = {
                'Line Crossing': 0,
                'Reverse Line Crossing': 0,
                'year': d.year,
                'day_number': dayNumber
            };
        }

        // Aggregate total count by event type
        tempAggregation[day][eventType] += totalCount;
    });

    // Convert temp aggregation into the original structure format
    const aggregatedData = [];
    for (const day in tempAggregation) {
        aggregatedData.push({
            day: day,
            day_number: tempAggregation[day].day_number,
            total_count: tempAggregation[day]['Line Crossing'].toString(), // Keep the count for the first event type as reference
            camera_id: selectedCameraId === 'All' ? 'All' : filteredData[0]?.camera_id, // Either 'All' or the selected camera name
            event_types: {
                'Line Crossing': tempAggregation[day]['Line Crossing'],
                'Reverse Line Crossing': tempAggregation[day]['Reverse Line Crossing']
            }
        });
    }

    return aggregatedData;
};

export const getTotalCountVisitors = (data, selectedPeriod, propertyName) => {
    const lineCrossing = data.find(d => d[propertyName] === selectedPeriod)?.event_types['Line Crossing'] || 0;
    const reverseLineCrossing = data.find(d => d[propertyName] === selectedPeriod)?.event_types['Reverse Line Crossing'] || 0;
    return Math.max(lineCrossing, reverseLineCrossing);
};

export const getCountPercentageIncrease = (data, selectedPeriod, propertyName, eventType) => {
    const currentIndex = data.findIndex(d => d[propertyName] === selectedPeriod);
    const previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
        return 100;
    }
    const currentTotal = data[currentIndex].event_types[eventType];
    const previousTotal = data[previousIndex].event_types[eventType];
    if (previousTotal > 0) {
        const perIncrease = ((currentTotal - previousTotal) / previousTotal) * 100;
        return perIncrease.toFixed(2);
    } else {
        return 100;
    }
}

export const getTotalCountForCurrentYear = (aggregatedMonthlyData, eventType) => {
    // getDate
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;

    const startOfYear = new Date(currentDate.getFullYear(), 0, 0);

    // Filter the data for the current calendar year (from January 1st to today)
    const filteredData = aggregatedMonthlyData.filter(d => {
        const monthDate = new Date(currentDate.getFullYear(), d.month - 1, 1);
        return monthDate >= startOfYear && monthDate <= currentDate;
    });

    // Sum up the events for the 'Reverse Line Crossing' event types
    const total = filteredData.reduce((acc, curr) => {
        const crossing = curr.event_types[eventType] || 0;
        return acc + crossing;
    }, 0);

    return total;
};

export const getTotalMonthlyAverageDwellTime = (dwellData, selectedMonth) => {
    const selectedMonthData = dwellData[selectedMonth -1];

    if (selectedMonthData.length === 0) {
        return 0;
    }


}

export const getAverageDwellTime = (dwellData, index) => {
    // Check if dwellData exists and is an array
    if (!dwellData || !Array.isArray(dwellData)) {
      return 0;
    }
    
    // Check if the specific index exists
    if (index < 0 || index >= dwellData.length || !dwellData[index]) {
      return 0;
    }
    
    // Check if the data at this index is an array and has items
    const timeSlotData = dwellData[index];
    if (!Array.isArray(timeSlotData) || timeSlotData.length === 0) {
      return 0;
    }
    
    // Calculate the average dwell time for this time slot
    let totalDwellTime = 0;
    let count = 0;
    
    for (let i = 0; i < timeSlotData.length; i++) {
      const item = timeSlotData[i];
      if (item && typeof item.average_dwell_time === 'number') {
        totalDwellTime += item.average_dwell_time;
        count++;
      }
    }
    
    return count > 0 ? Math.round(totalDwellTime / count) : 0;
  };

  export const getDayIndex = (dayName) => {
    const dayMapping = {
      'Monday': 0,
      'Tuesday': 1,
      'Wednesday': 2,
      'Thursday': 3,
      'Friday': 4,
      'Saturday': 5,
      'Sunday': 6
    };
    
    return dayName in dayMapping ? dayMapping[dayName] : 0;
  };

export const getDwellTimePercentChange = (dwellData, index) => {
    // Get current average
    const currentAverage = getAverageDwellTime(dwellData, index);
    
    // If there's no data for the current period, return empty string
    if (currentAverage === 0) {
      return '';
    }
    
    // Get previous average (assumed to be the previous index)
    const prevIndex = index - 1;
    const prevAverage = prevIndex >= 0 ? getAverageDwellTime(dwellData, prevIndex) : 0;
    
    // If there's no data for the previous period, return empty string
    if (prevAverage === 0) {
      return '';
    }
    
    // Calculate percentage change
    const percentChange = ((currentAverage - prevAverage) / prevAverage) * 100;
    
    // Return formatted percentage (limit to 1 decimal point)
    return percentChange.toFixed(1);
  };

  export const filterDwellData = (dwellData, cameraId) => {
    // If no data or not an array, return empty array
    if (!dwellData || !Array.isArray(dwellData)) {
      return [];
    }
    
    // Create a new array with the same structure but filter by camera ID
    return dwellData.map(timeSlot => {
      if (!Array.isArray(timeSlot)) {
        return [];
      }
      
      // If no camera ID specified, return all data
      if (!cameraId) {
        return timeSlot;
      }
      
      // Filter by camera ID
      return timeSlot.filter(item => item && item.camera_id === cameraId);
    });
  };

export const filterMonthlyDwellData = (dwellData, selectedCameraId) => {
    if (dwellData.length === 0) {
        return [];
    }

    let filteredData = [];

    let cameraHasData = false;

    if (selectedCameraId === 'All') {
        filteredData = dwellData;
        cameraHasData = true;
    } else {
        for (let i = 0; i < dwellData.length; i++) {
            const monthData = dwellData[i];
            const filteredMonthData = [];
            for (let j = 0; j < monthData.length; j++ ) {
                const polygonData = monthData[j];
                if (polygonData.camera_id === selectedCameraId) {
                    filteredMonthData.push(polygonData);
                    cameraHasData = true;
                }
            }
            filteredData.push(filteredMonthData);
        }
    }

    if (!cameraHasData) {
        // console.log("This camera has no MONTHLY data ;_;");
        return [];
    }

    return filteredData;
}

export const checkForNoDwellData = (dwellData) => {
    // Check if data exists and is an array
    if (!dwellData || !Array.isArray(dwellData)) {
      return false;
    }
    
    // Check if any time slot has data
    for (let i = 0; i < dwellData.length; i++) {
      const timeSlot = dwellData[i];
      if (Array.isArray(timeSlot) && timeSlot.length > 0) {
        return true;
      }
    }
    
    // No data found
    return false;
  };

export const checkForEmptyData = (data) => {
    let hasData = false;

    for (let i = 0; i < data.length; i++) {
        const row = data[i];

        if (row.total_count > 0) {
            hasData = true;
        }
    }
    return hasData;
}

export const getTotalForPeriod = (data, selectedPeriod, periodName, targetValue ) => {
    const row = data.find(d => d[periodName] === selectedPeriod);
    if (row) {
        return row[targetValue];
    } else {
        return 0;
    }
};

export const getPercentageIncrease = (data, selectedPeriod, periodName, targetValue) => {
    const currentIndex = data.findIndex(d => d[periodName] === selectedPeriod);
    const previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
        return 0;
    }
    const currentTotal = data[currentIndex][targetValue];
    const previousTotal = data[previousIndex][targetValue];
    if (previousTotal > 0) {
        const perIncrease = ((currentTotal - previousTotal) / previousTotal) * 100;
        return perIncrease.toFixed(2);
    } else {
        return 100;
    }
};

export const getYearlyTotal = (data, targetValue) => {
    const total = data.reduce((sum, d) => sum + (parseInt(d[targetValue]) || 0), 0);
    return total
};
